import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Bed, Bath, Square, Phone, Mail, Calendar, Heart, Share2, ChevronLeft } from 'lucide-react';

const PropertyDetail = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock data - in a real app, this would come from an API
    const mockProperties = {
      1: {
        id: 1,
        title: 'Modern Downtown Apartment',
        price: 450000,
        location: 'Manhattan, NY',
        beds: 2,
        baths: 2,
        sqft: 1200,
        type: 'apartment',
        yearBuilt: 2019,
        images: [
          'https://images.unsplash.com/photo-1560448204-e02f11c3d0e7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
          'https://images.unsplash.com/photo-1560185007-c5ca9d2c014d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
          'https://images.unsplash.com/photo-1564013799919-600e892d9f7f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
        ],
        featured: true,
        description: 'Stunning modern apartment in the heart of Manhattan with panoramic city views. This beautifully designed space features floor-to-ceiling windows, hardwood floors throughout, and a gourmet kitchen with top-of-the-line appliances.',
        features: [
          'Floor-to-ceiling windows',
          'Hardwood floors',
          'Gourmet kitchen',
          'In-unit laundry',
          'Central air conditioning',
          'Doorman building',
          'Gym access',
          'Rooftop terrace',
          'Storage unit',
          'Pet-friendly'
        ],
        agent: {
          name: 'Lisa Johnson',
          phone: '(555) 123-4567',
          email: 'lisa@lisarealestate.com',
          image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80'
        }
      },
      2: {
        id: 2,
        title: 'Suburban Family Home',
        price: 750000,
        location: 'Brooklyn, NY',
        beds: 4,
        baths: 3,
        sqft: 2500,
        type: 'house',
        yearBuilt: 2015,
        images: [
          'https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
          'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
        ],
        featured: true,
        description: 'Beautiful family home with large backyard and modern amenities. Perfect for growing families, this home offers plenty of space, natural light, and a great neighborhood.',
        features: [
          'Large backyard',
          'Modern kitchen',
          'Master suite',
          'Finished basement',
          '2-car garage',
          'Hardwood floors',
          'Central air',
          'Fireplace',
          'Walk-in closets',
          'School district A+'
        ],
        agent: {
          name: 'Lisa Johnson',
          phone: '(555) 123-4567',
          email: 'lisa@lisarealestate.com',
          image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80'
        }
      },
      3: {
        id: 3,
        title: 'Luxury Penthouse',
        price: 1250000,
        location: 'Upper East Side, NY',
        beds: 3,
        baths: 3,
        sqft: 2000,
        type: 'apartment',
        yearBuilt: 2020,
        images: [
          'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80'
        ],
        featured: true,
        description: 'Luxurious penthouse with breathtaking views of Central Park. This exclusive property offers the ultimate in luxury living with premium finishes and world-class amenities.',
        features: [
          'Central Park views',
          'Private terrace',
          'Marble bathrooms',
          'Wine cellar',
          'Smart home technology',
          'Concierge service',
          'Valet parking',
          'Spa access',
          'Private elevator',
          '24/7 security'
        ],
        agent: {
          name: 'Lisa Johnson',
          phone: '(555) 123-4567',
          email: 'lisa@lisarealestate.com',
          image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80'
        }
      }
    };

    // Simulate API call
    setTimeout(() => {
      const foundProperty = mockProperties[id];
      setProperty(foundProperty);
      setLoading(false);
    }, 500);
  }, [id]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Not Found</h2>
          <p className="text-gray-600 mb-8">The property you're looking for doesn't exist.</p>
          <Link to="/properties" className="btn-primary">
            Back to Properties
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link to="/properties" className="inline-flex items-center text-gray-600 hover:text-primary-600 mb-6">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Properties
        </Link>

        {/* Property Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Gallery */}
            <div className="relative">
              <img
                src={property.images[0]}
                alt={property.title}
                className="w-full h-96 lg:h-full object-cover"
              />
              <div className="absolute top-4 left-4 flex space-x-2">
                {property.featured && (
                  <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Featured
                  </span>
                )}
                <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  For Sale
                </span>
              </div>
              <div className="absolute top-4 right-4 flex space-x-2">
                <button className="bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors">
                  <Heart className="h-4 w-4 text-gray-600" />
                </button>
                <button className="bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors">
                  <Share2 className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Property Info */}
            <div className="p-6 lg:p-8">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.title}</h1>
                <div className="flex items-center text-gray-600 mb-4">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span>{property.location}</span>
                </div>
                <div className="text-3xl font-bold text-primary-600 mb-6">
                  {formatPrice(property.price)}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Bed className="h-6 w-6 text-primary-600 mx-auto mb-2" />
                  <div className="text-xl font-semibold text-gray-900">{property.beds}</div>
                  <div className="text-sm text-gray-600">Bedrooms</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Bath className="h-6 w-6 text-primary-600 mx-auto mb-2" />
                  <div className="text-xl font-semibold text-gray-900">{property.baths}</div>
                  <div className="text-sm text-gray-600">Bathrooms</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Square className="h-6 w-6 text-primary-600 mx-auto mb-2" />
                  <div className="text-xl font-semibold text-gray-900">{property.sqft}</div>
                  <div className="text-sm text-gray-600">Square Feet</div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                <p className="text-gray-600 leading-relaxed">{property.description}</p>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Property Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Property Type:</span>
                    <span className="font-medium text-gray-900 capitalize">{property.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Year Built:</span>
                    <span className="font-medium text-gray-900">{property.yearBuilt}</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Features</h3>
                <div className="grid grid-cols-2 gap-2">
                  {property.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-gray-600">
                      <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Agent Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 lg:p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Agent</h3>
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6">
            <img
              src={property.agent.image}
              alt={property.agent.name}
              className="w-24 h-24 rounded-full object-cover"
            />
            <div className="flex-grow">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">{property.agent.name}</h4>
              <p className="text-gray-600 mb-4">Senior Real Estate Agent</p>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <a
                  href={`tel:${property.agent.phone}`}
                  className="btn-primary inline-flex items-center justify-center"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  {property.agent.phone}
                </a>
                <a
                  href={`mailto:${property.agent.email}`}
                  className="btn-secondary inline-flex items-center justify-center"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
